import React from 'react';

import config from '../config';
import {ACCOUNT_ROUTES_DATA} from './account/accountRoutesData';
import {ADMIN_ROUTES_DATA} from './admin/adminRoutesData';
import {ARTIFACT_VERSION_PATH} from './artifacts/artifactPaths';
import {AUTH_ROUTES_DATA} from './auth/authRoutesData';
import {BENCHMARK_ROUTES_DATA} from './benchmark/benchmarkRoutesData';
import {
  INFERENCE_COMPARE_PATH,
  INFERENCE_PATH,
} from './inference/inferencePaths';
import {LAUNCH_ROUTES_DATA} from './launch/launchRoutesData';
import {
  ACADEMIC_APPLICATION_ROUTE_DATA,
  ONBOARDING_ROUTES_DATA,
  TRIAL_END_ROUTE_DATA,
} from './onboarding/onboardingRoutesData';
import {
  ORG_API_KEYS,
  ORG_BILLING,
  ORG_DASHBOARD,
  ORG_SERVICE_ACCOUNTS,
  ORG_SETTINGS,
  ORG_SETTINGS_PATHS,
  ORG_TEAMS,
  ORG_USER_ACTIVITY,
} from './organization/orgPaths';
import {HOME_PATH, NO_ACCESS_PATH, PROJECT_PATHS, REDIRECT_PATH} from './paths';
import {REGISTRY_PATHS} from './registry/registryPaths';
import {
  GALLERY_PATH,
  GALLERY_PATH_SEGMENT,
  OLD_GALLERY_DISCUSSION_EDIT_PATH,
  OLD_GALLERY_DISCUSSION_VIEW_PATH,
  OLD_REPORT_PAGE_EDIT_PATH,
  OLD_REPORT_PAGE_VIEW_PATH,
  REPORT_PAGE_EDIT_PATH,
  REPORT_PAGE_VIEW_PATH,
  REPORT_PAGE_VIEW_PUBLISHED_WORK_PATH,
} from './report/reportPaths';
import {
  PROFILE_PATH,
  PROFILE_PROJECTS_PATH,
  PROFILE_REPORTS_PATH,
  PROFILE_STARS_PATH,
  TEAM_MEMBERS_PATH,
  TEAM_SERVICE_ACCOUNTS_PATH,
  TEAM_SETTINGS_PATH,
} from './team/teamPaths';
import {RouteData} from './types';
import {
  GLOBAL_HISTORIC_USAGE_PATH,
  GLOBAL_HISTORIC_USAGE_REDIRECT_PATH,
  GLOBAL_HISTORIC_USAGE_REDIRECT_TO_PATH,
  HISTORIC_USAGE_PATH,
  HISTORIC_USAGE_REDIRECT_PATH,
  HISTORIC_USAGE_REDIRECT_TO_PATH,
  STORAGE_EXPLORER_TRACKED_PATH,
  USAGE_PATH,
} from './usage/usagePaths';
import {GROUP_PATHS} from './workspace/groupWorkspacePaths';
import {PROJECT_BASE_PATH} from './workspace/multiRunWorkspacePaths';
import {RUN_PATHS} from './workspace/singleRunWorkspacePaths';
import {SWEEP_ROUTES_DATA} from './workspace/sweepRoutesData';

const IS_PRIVATE_ENV = config.ENVIRONMENT_IS_PRIVATE;

export const ROUTES_DATA: RouteData[] = [
  {
    additionalProps: {
      allowIframes: false,
      routeName: 'Home',
    },
    exact: true,
    isPrivateRoute: false,
    path: '/',
    RouteComponent: React.lazy(
      () => import('../pages/HomePage/HomePageRouter')
    ),
  },
  {
    additionalProps: {
      allowIframes: false,
      routeName: 'Home',
    },
    exact: true,
    isPrivateRoute: true,
    path: HOME_PATH,
    RouteComponent: React.lazy(
      () => import('../pages/HomePage/HomePageWithData')
    ),
  },
  ...AUTH_ROUTES_DATA,
  ...ONBOARDING_ROUTES_DATA,
  ...ADMIN_ROUTES_DATA,
  {
    additionalProps: {
      allowIframes: false,
      routeName: 'FullyConnected',
    },
    isPrivateRoute: false,
    path: GALLERY_PATH,
    RouteComponent: React.lazy(() => import('../pages/FC')),
  },
  {
    path: '/fullyconnected',
    redirect: `/${GALLERY_PATH_SEGMENT}`,
  },
  {
    path: '/fc',
    redirect: `/${GALLERY_PATH_SEGMENT}`,
  },
  {
    path: '/gallery',
    redirect: `/${GALLERY_PATH_SEGMENT}`,
  },
  {
    additionalProps: {
      allowIframes: false,
      routeName: 'InferenceModel',
    },
    isPrivateRoute: false,
    path: `${INFERENCE_PATH}/:collectionId/:modelId`,
    RouteComponent: React.lazy(
      () => import('../pages/HomePage/HomePageWithData')
    ),
  },
  {
    additionalProps: {
      allowIframes: false,
      routeName: 'Inference',
    },
    isPrivateRoute: false,
    path: `${INFERENCE_PATH}/:collectionId?`,
    RouteComponent: React.lazy(
      () => import('../pages/HomePage/HomePageWithData')
    ),
  },
  {
    additionalProps: {
      allowIframes: false,
      routeName: 'Inference Compare',
    },
    isPrivateRoute: false,
    path: INFERENCE_COMPARE_PATH,
    RouteComponent: React.lazy(
      () => import('../pages/HomePage/HomePageWithData')
    ),
  },
  {
    // the entity named "weave" is claimed by a past wandb employee
    // this is doing the quick thing to redirect this path to the weave site instead
    additionalProps: {
      allowIframes: false,
      routeName: 'WeaveEntity',
    },
    exact: true,
    isPrivateRoute: false,
    path: '/weave',
    RouteComponent: React.lazy(
      () => import('../pages/EntityPage/RedirectToWeaveSite')
    ),
  },
  {
    additionalProps: {
      allowIframes: false,
      routeName: 'ErrorTestPage',
    },
    exact: true,
    isPrivateRoute: false,
    path: '/__test/simple_error',
    RouteComponent: React.lazy(() => import('../pages/ErrorTestPage')),
  },
  ...ACCOUNT_ROUTES_DATA,
  {
    additionalProps: {
      allowIframes: false,
      routeName: 'CreateNewTeam',
    },
    condition: !IS_PRIVATE_ENV,
    exact: true,
    isPrivateRoute: true,
    path: '/create-team',
    RouteComponent: React.lazy(() => import('../pages/CreateNewTeamPage')),
  },
  {
    additionalProps: {
      allowIframes: false,
      routeName: 'OrgDashboard',
    },
    condition: IS_PRIVATE_ENV,
    exact: true,
    isPrivateRoute: true,
    path: ORG_DASHBOARD,
    RouteComponent: React.lazy(
      () => import('../pages/HomePage/HomePageWithData')
    ),
  },
  {
    additionalProps: {
      allowIframes: false,
      routeName: 'OrgTeams',
    },
    condition: IS_PRIVATE_ENV,
    exact: true,
    isPrivateRoute: true,
    path: ORG_TEAMS,
    RouteComponent: React.lazy(
      () => import('../pages/HomePage/HomePageWithData')
    ),
  },
  {
    additionalProps: {
      allowIframes: false,
      routeName: 'OrgServiceAccounts',
    },
    condition: IS_PRIVATE_ENV,
    exact: true,
    isPrivateRoute: true,
    path: ORG_SERVICE_ACCOUNTS,
    RouteComponent: React.lazy(
      () => import('../pages/HomePage/HomePageWithData')
    ),
  },
  {
    additionalProps: {
      allowIframes: false,
      routeName: 'OrgApiKeys',
    },
    condition: IS_PRIVATE_ENV,
    exact: true,
    isPrivateRoute: true,
    path: ORG_API_KEYS,
    RouteComponent: React.lazy(
      () => import('../pages/HomePage/HomePageWithData')
    ),
  },
  {
    additionalProps: {
      allowIframes: false,
      routeName: 'OrgActivity',
    },
    condition: IS_PRIVATE_ENV,
    exact: true,
    isPrivateRoute: true,
    path: ORG_USER_ACTIVITY,
    RouteComponent: React.lazy(
      () => import('../pages/HomePage/HomePageWithData')
    ),
  },
  {
    additionalProps: {
      allowIframes: false,
      routeName: 'OrgBilling',
    },
    condition: IS_PRIVATE_ENV,
    exact: true,
    isPrivateRoute: true,
    path: ORG_BILLING,
    RouteComponent: React.lazy(
      () => import('../pages/HomePage/HomePageWithData')
    ),
  },
  {
    path: '/org/roles',
    redirect: ORG_SETTINGS,
  },
  {
    additionalProps: {
      allowIframes: false,
      routeName: 'OrgSettingsPage',
    },
    condition: IS_PRIVATE_ENV,
    exact: true,
    isPrivateRoute: true,
    path: ORG_SETTINGS_PATHS,
    RouteComponent: React.lazy(
      () => import('../pages/HomePage/HomePageWithData')
    ),
  },

  {
    additionalProps: {
      allowIframes: false,
      routeName: 'StorageExplorer',
    },
    exact: true,
    isPrivateRoute: false,
    path: [
      USAGE_PATH,
      STORAGE_EXPLORER_TRACKED_PATH,
      '/usage/:entityName/:filePath*',
    ],
    RouteComponent: React.lazy(() => import('../pages/StorageExplorer')),
  },
  {
    path: '/storage/:entityName/tracked/:filePath*',
    redirect: STORAGE_EXPLORER_TRACKED_PATH,
  },
  {
    path: '/storage/:entityName/:filePath*',
    redirect: '/usage/:entityName/:filePath*',
  },
  {
    path: '/storage/:entityName',
    redirect: '/usage/:entityName',
  },
  ...BENCHMARK_ROUTES_DATA,
  {
    additionalProps: {allowIframes: false, routeName: 'Entity', tab: 'reports'},
    exact: true,
    isPrivateRoute: false,
    path: PROFILE_REPORTS_PATH,
    RouteComponent: React.lazy(() => import('../pages/EntityPage')),
    // note that here and below, we rely on the fact that we include the tab
    // when we log the route name
  },
  {
    additionalProps: {
      allowIframes: false,
      routeName: 'Entity',
      tab: 'projects',
    },
    exact: true,
    isPrivateRoute: false,
    path: PROFILE_PROJECTS_PATH,
    RouteComponent: React.lazy(() => import('../pages/EntityPage')),
  },
  {
    additionalProps: {allowIframes: false, routeName: 'Entity', tab: 'stars'},
    exact: true,
    isPrivateRoute: false,
    path: PROFILE_STARS_PATH,
    RouteComponent: React.lazy(() => import('../pages/EntityPage')),
  },
  {
    additionalProps: {allowIframes: false, routeName: 'Entity', tab: 'members'},
    exact: true,
    isPrivateRoute: false,
    path: TEAM_MEMBERS_PATH,
    RouteComponent: React.lazy(() => import('../pages/EntityPage')),
  },
  {
    additionalProps: {
      allowIframes: false,
      routeName: 'Entity',
      tab: 'service-accounts',
    },
    exact: true,
    isPrivateRoute: false,
    path: TEAM_SERVICE_ACCOUNTS_PATH,
    RouteComponent: React.lazy(() => import('../pages/EntityPage')),
  },
  {
    additionalProps: {
      allowIframes: false,
      routeName: 'Entity',
      tab: 'settings',
    },
    exact: true,
    isPrivateRoute: false,
    path: TEAM_SETTINGS_PATH,
    RouteComponent: React.lazy(() => import('../pages/EntityPage')),
  },
  // TODO: Remove! Keeping for backwards compatibility (email links, etc)
  {
    additionalProps: {
      allowIframes: false,
      routeName: 'Entity',
      tab: 'settings',
    },
    exact: true,
    isPrivateRoute: false,
    path: '/teams/:entityName',
    RouteComponent: React.lazy(() => import('../pages/EntityPage')),
  },
  {
    additionalProps: {
      allowIframes: false,
      routeName: 'Registries',
    },
    exact: true,
    isPrivateRoute: false,
    path: REGISTRY_PATHS,
    RouteComponent: React.lazy(
      () => import('../components/Registries/RegistriesRouter')
    ),
  },
  {
    path: `${ARTIFACT_VERSION_PATH}/graph`,
    redirect: `${ARTIFACT_VERSION_PATH}/lineage`,
  },
  {
    path: '/models',
    redirect: '/registry/model',
  },
  ...LAUNCH_ROUTES_DATA,
  {
    additionalProps: {
      allowIframes: true,
      routeName: 'Run',
    },
    exact: true,
    isPrivateRoute: false,
    path: RUN_PATHS,
    RouteComponent: React.lazy(() => import('../pages/RunPage2')),
  },
  {
    additionalProps: {
      allowIframes: true,
      routeName: 'Group',
    },
    exact: true,
    isPrivateRoute: false,
    path: GROUP_PATHS,
    RouteComponent: React.lazy(() => import('../pages/GroupPage')),
  },
  ...SWEEP_ROUTES_DATA,
  {
    // This old edit report page route will be redirected to /reportlist
    additionalProps: {
      allowIframes: false,
      routeName: 'OldReportPageEdit',
    },
    exact: true,
    isPrivateRoute: false,
    path: OLD_REPORT_PAGE_EDIT_PATH,
    RouteComponent: React.lazy(
      () => import('../pages/ReportPage/ReportPageRouter')
    ),
  },
  {
    // This old view report page route will be redirected to /reportlist
    additionalProps: {
      allowIframes: false,
      routeName: 'OldReportPageView',
    },
    exact: true,
    isPrivateRoute: false,
    path: OLD_REPORT_PAGE_VIEW_PATH,
    RouteComponent: React.lazy(
      () => import('../pages/ReportPage/ReportPageRouter')
    ),
  },
  {
    additionalProps: {
      allowIframes: true,
      routeName: 'ReportEdit',
    },
    exact: true,
    isPrivateRoute: false,
    path: REPORT_PAGE_EDIT_PATH,
    RouteComponent: React.lazy(
      () => import('../pages/ReportPage/ReportPageRouter')
    ),
  },
  {
    additionalProps: {
      allowIframes: true,
      routeName: 'ReportView',
    },
    exact: true,
    isPrivateRoute: false,
    path: REPORT_PAGE_VIEW_PATH,
    RouteComponent: React.lazy(
      () => import('../pages/ReportPage/ReportPageRouter')
    ),
  },
  {
    additionalProps: {
      allowIframes: true,
      routeName: 'ReportViewPublished',
    },
    exact: true,
    isPrivateRoute: false,
    path: REPORT_PAGE_VIEW_PUBLISHED_WORK_PATH,
    RouteComponent: React.lazy(
      () => import('../pages/ReportPage/ReportPageRouter')
    ),
  },
  {
    additionalProps: {
      allowIframes: false,
      routeName: 'FullyConnectedDiscussionEdit',
    },
    exact: true,
    isPrivateRoute: false,
    path: OLD_GALLERY_DISCUSSION_EDIT_PATH,
    RouteComponent: React.lazy(
      () => import('../pages/ReportPage/ReportPageRouter')
    ),
  },
  {
    additionalProps: {
      allowIframes: false,
      routeName: 'FullyConnectedDiscussionView',
    },
    exact: true,
    isPrivateRoute: false,
    path: OLD_GALLERY_DISCUSSION_VIEW_PATH,
    RouteComponent: React.lazy(
      () => import('../pages/ReportPage/ReportPageRouter')
    ),
  },
  {
    additionalProps: {
      allowIframes: false,
      routeName: 'NoAccess',
    },
    exact: true,
    isPrivateRoute: false,
    path: NO_ACCESS_PATH,
    RouteComponent: React.lazy(() => import('../pages/NoAccessPage')),
  },
  {
    additionalProps: {
      allowIframes: false,
      routeName: 'GlobalHistoricUsage',
    },
    condition: IS_PRIVATE_ENV,
    exact: true,
    isPrivateRoute: true,
    path: GLOBAL_HISTORIC_USAGE_PATH,
    RouteComponent: React.lazy(
      () => import('../pages/HistoricUsage/GlobalHistoricUsage')
    ),
  },
  {
    condition: IS_PRIVATE_ENV,
    path: GLOBAL_HISTORIC_USAGE_REDIRECT_PATH,
    redirect: GLOBAL_HISTORIC_USAGE_REDIRECT_TO_PATH,
  },
  {
    additionalProps: {
      allowIframes: false,
      routeName: 'OrganizationHistoricUsage',
    },
    condition: !IS_PRIVATE_ENV,
    exact: true,
    isPrivateRoute: true,
    path: HISTORIC_USAGE_PATH,
    RouteComponent: React.lazy(
      () => import('../pages/HistoricUsage/OrganizationHistoricUsage')
    ),
  },
  {
    condition: !IS_PRIVATE_ENV,
    path: HISTORIC_USAGE_REDIRECT_PATH,
    redirect: HISTORIC_USAGE_REDIRECT_TO_PATH,
  },
  {
    additionalProps: {
      allowIframes: true,
      routeName: 'Workspace',
    },
    exact: true,
    isPrivateRoute: false,
    path: PROJECT_PATHS,
    RouteComponent: React.lazy(() => import('../pages/ProjectPage')),
  },
  {
    additionalProps: {
      allowIframes: false,
      routeName: 'Redirect',
    },
    exact: true,
    isPrivateRoute: false,
    path: REDIRECT_PATH,
    RouteComponent: React.lazy(
      () => import('../pages/RedirectPage/RedirectPage')
    ),
  },
  {
    // this is here to truncate extra path segments after the actual path
    path: PROJECT_BASE_PATH,
    redirect: PROJECT_BASE_PATH,
  },
  TRIAL_END_ROUTE_DATA,
  ACADEMIC_APPLICATION_ROUTE_DATA,
  {
    additionalProps: {
      allowIframes: false,
      routeName: 'Entity',
    },
    exact: true,
    isPrivateRoute: false,
    path: PROFILE_PATH,
    RouteComponent: React.lazy(() => import('../pages/EntityPage')),
  },
];
